import * as Popover from '@radix-ui/react-popover';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useMemo } from 'react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import {
  canUseFeature,
  features,
  getCheapestPlanForFeature,
  shouldHideFeature,
} from '@dashboard/services/features';

import Button from './Button';

const UpgradePopover = ({ children }) => <Popover className="relative">{children}</Popover>;

const UpgradePopoverPanel = ({ plan, feature, side, align, ...props }) => {
  const featureKey = Object.keys(features).find(key => features[key] === feature);

  return (
    <Popover.Portal>
      <Popover.Content
        {...props}
        side={side}
        align={align}
        className={clsx(
          'z-10 bg-white border border-gray-50 pt-4 px-3 pb-3 rounded w-72 shadow-lg outline-none my-2 mx-0',
        )}
      >
        <>
          <h2 className="flex justify-between items-center mb-1 text-xl font-bold">
            Upgrade to unlock
            <LockClosedIcon className="w-5 h-5 text-chocolate-600"></LockClosedIcon>
          </h2>

          <p className="mb-4 text-gray-700 pr-8">
            Upgrade to {plan.title} to use {feature.label}
          </p>

          <Button
            appearance="primary"
            to={`/upgrade?plan=${plan.handle}&upgradeFlow=feature-popover-${featureKey}`}
            size="full"
          >
            Upgrade
          </Button>
        </>
      </Popover.Content>
    </Popover.Portal>
  );
};

const UpgradePopoverButton = ({ children, ...props }) => (
  <Popover.Trigger asChild {...props}>
    {children}
  </Popover.Trigger>
);

UpgradePopover.Button = UpgradePopoverButton;
UpgradePopover.Panel = UpgradePopoverPanel;

export function useCanUseFeature(feature) {
  const { plan } = useAuth();

  return canUseFeature(feature, plan.handle);
}

const CanUseFeature = ({
  feature,
  align = 'start',
  isDisabled: isDisabledProp,
  inset = false,
  reduceOpacity = true,
  iconSize = 'default',
  showIcon = true,
  children,
}) => {
  const { plan, isAuthenticating, sellerId } = useAuth();

  const canUse = useMemo(
    () => canUseFeature(feature, plan.handle, sellerId),
    [feature, plan?.handle],
  );

  const shouldHide = useMemo(() => shouldHideFeature(feature, canUse), [feature, canUse]);

  if (isAuthenticating) return <>{children}</>;

  if (shouldHide) return null;

  const isDisabled = isDisabledProp || !canUse;

  if (isDisabled) {
    return (
      <Popover.Root>
        <UpgradePopover.Button className="relative w-full text-left cursor-pointer">
          <div className="w-full flex justify-between">
            <div
              className={clsx(`pointer-events-none w-full`, {
                'opacity-50': reduceOpacity,
              })}
            >
              {children}
            </div>

            {showIcon && (
              <div
                className={clsx('absolute', {
                  'top-px right-px': !inset,
                  'top-2 right-2': inset,
                })}
              >
                <LockClosedIcon
                  className={clsx('text-chocolate-600 bg-white rounded-tr-lg', {
                    'w-4 h-4': iconSize === 'small',
                    'w-5 h-5': iconSize === 'default',
                  })}
                />
              </div>
            )}
          </div>
        </UpgradePopover.Button>

        <UpgradePopover.Panel
          size="right"
          align={align}
          className="mb-0"
          plan={getCheapestPlanForFeature(feature)}
          feature={features[feature]}
        />
      </Popover.Root>
    );
  }

  return <>{children}</>;
};

export default CanUseFeature;
